import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { MessageCircle, Mail } from "lucide-react";
import { waNumber } from "@/components/donors/LateContactDialog";
import { fmtDate } from "@/lib/format";

const lesson = (r) => `${r.course_name || "lezione"} del ${fmtDate(r.date)}${r.start_time ? ` (${r.start_time}–${r.end_time})` : ""}`;

function message(r, toGuardian) {
  const who = toGuardian ? `Gentile ${r.guardian_name || "famiglia"},\nle segnaliamo che ${r.student_name}` : `Ciao ${r.student_name},\nrisulti`;
  return `${who} ${toGuardian ? "risulta " : ""}assente non giustificato/a alla ${lesson(r)}.\nTi chiediamo di inviare la giustificazione alla segreteria il prima possibile.\n\nSegreteria RDM ClassRoom`;
}

function ContactButton({ icon: Icon, label, href, onUse, testId }) {
  if (!href) return null;
  return (
    <Button variant="outline" className="justify-start gap-2" data-testid={testId}
      onClick={() => { window.open(href, "_blank", "noopener"); onUse(); }}>
      <Icon className="w-4 h-4" /> {label}
    </Button>
  );
}

export function ContactDialog({ row, onClose, onReported }) {
  if (!row) return null;
  const subject = `Assenza non giustificata — ${lesson(row)}`;
  const wa = (p, g) => (p && waNumber(p).length >= 8 ? `https://wa.me/${waNumber(p)}?text=${encodeURIComponent(message(row, g))}` : null);
  const mail = (e, g) => (e ? `mailto:${e}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(message(row, g))}` : null);
  const opts = [
    { icon: MessageCircle, label: "WhatsApp allo studente", href: wa(row.phone, false), ch: "whatsapp", id: "contact-wa-student" },
    { icon: Mail, label: "Email allo studente", href: mail(row.email, false), ch: "email", id: "contact-email-student" },
    { icon: MessageCircle, label: "WhatsApp al genitore/tutore", href: wa(row.guardian_phone, true), ch: "whatsapp", id: "contact-wa-guardian" },
    { icon: Mail, label: "Email al genitore/tutore", href: mail(row.guardian_email, true), ch: "email", id: "contact-email-guardian" },
  ];
  const none = !opts.some((o) => o.href);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="absence-contact-dialog">
        <DialogHeader>
          <DialogTitle>Segnala assenza</DialogTitle>
          <DialogDescription>{row.student_name} — {lesson(row)}. Il messaggio è già scritto: dopo l'invio l'assenza risulta segnalata.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-2">
          {opts.map((o) => (
            <ContactButton key={o.id} icon={o.icon} label={o.label} href={o.href} testId={o.id} onUse={() => onReported(o.ch)} />
          ))}
          {none && <p className="text-sm text-stone-500" data-testid="contact-none">Nessun telefono o email in anagrafica per questo studente.</p>}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function JustifyDialog({ row, onClose, onConfirm, pending }) {
  const [reason, setReason] = useState("");
  const [partial, setPartial] = useState(false);
  const [from, setFrom] = useState(row?.start_time || "");
  const [to, setTo] = useState(row?.arrival_time || row?.end_time || "");
  if (!row) return null;
  const badTime = partial && (!from || !to || from >= to);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="absence-justify-dialog">
        <DialogHeader>
          <DialogTitle>Giustifica assenza</DialogTitle>
          <DialogDescription>{row.student_name} — {lesson(row)}</DialogDescription>
        </DialogHeader>
        <div className="flex gap-2">
          <Button type="button" size="sm" variant={partial ? "outline" : "default"} onClick={() => setPartial(false)} data-testid="justify-mode-full">Tutta la lezione</Button>
          <Button type="button" size="sm" variant={partial ? "default" : "outline"} onClick={() => setPartial(true)} data-testid="justify-mode-partial">Solo alcune ore</Button>
        </div>
        {partial && (
          <div className="flex items-end gap-2">
            <label className="text-xs text-stone-500">Dalle<Input type="time" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="justify-from-input" /></label>
            <label className="text-xs text-stone-500">Alle<Input type="time" value={to} onChange={(e) => setTo(e.target.value)} data-testid="justify-to-input" /></label>
            <p className="text-xs text-stone-500 pb-2">Il resto della lezione resta da controllare.</p>
          </div>
        )}
        <Label htmlFor="justify-reason">Motivo</Label>
        <Textarea id="justify-reason" value={reason} onChange={(e) => setReason(e.target.value)}
          placeholder="Es. certificato medico, visita, motivi familiari…" data-testid="justify-reason-input" />
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="justify-cancel-btn">Annulla</Button>
          <Button disabled={!reason.trim() || pending || badTime} onClick={() => onConfirm(reason, partial ? { time_from: from, time_to: to } : {})} data-testid="justify-confirm-btn">
            {partial ? "Giustifica queste ore" : "Segna giustificata"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
